import {
  Award,
  Banknote,
  Gift,
  Receipt,
  BarChart3,
  BriefcaseBusiness,
  Building2,
  Car,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Clock,
  Database,
  FileText,
  Fuel,
  GraduationCap,
  HeartPulse,
  History,
  Layers,
  LayoutDashboard,
  Laptop,
  LogOut,
  MapPin,
  MessageCircle,
  Smartphone,
  Megaphone,
  Network,
  ReceiptText,
  Scale,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Tag,
  Target,
  UserCog,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { PERMISSIONS, type Permission } from '@hrms/shared';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * The item is shown when the user holds **any** of these. Omitted means
   * always visible to an authenticated user.
   *
   * This governs visibility only — the API is the actual authority. Hiding a
   * link is a courtesy, not a control; every route is separately guarded and
   * every endpoint re-checks.
   */
  permissions?: Permission[];
  /** Renders a "soon" chip for modules whose routes are not built yet. */
  planned?: boolean;
}

export interface NavSection {
  title: string;
  /**
   * Rendered open, without a toggle. Reserved for the handful of links that are
   * the reason people open the app at all — putting those behind a click would
   * trade one kind of friction for another.
   */
  pinned?: boolean;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Overview',
    pinned: true,
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard },
      {
        label: 'Analytics',
        to: '/analytics',
        icon: BarChart3,
        permissions: [PERMISSIONS.ANALYTICS_VIEW],
      },
    ],
  },
  {
    title: 'People',
    items: [
      {
        label: 'Employees',
        to: '/employees',
        icon: Users,
        permissions: [PERMISSIONS.EMPLOYEE_READ_ALL, PERMISSIONS.EMPLOYEE_READ_TEAM],
      },
      {
        label: 'Departments',
        to: '/departments',
        icon: Building2,
        permissions: [PERMISSIONS.DEPARTMENT_READ],
      },
      {
        label: 'Positions',
        to: '/positions',
        icon: BriefcaseBusiness,
        permissions: [PERMISSIONS.POSITION_READ],
      },
      {
        label: 'Salary Grades',
        to: '/salary-grades',
        icon: Layers,
        permissions: [PERMISSIONS.SALARY_GRADE_READ],
      },
      {
        label: 'Org Chart',
        to: '/org-chart',
        icon: Network,
        permissions: [PERMISSIONS.ORG_CHART_VIEW],
      },
      {
        label: 'Employee History',
        to: '/employee-history',
        icon: History,
        permissions: [PERMISSIONS.EMPLOYEE_HISTORY_READ_ALL],
      },
    ],
  },
  {
    title: 'Time',
    items: [
      {
        label: 'Work Schedules',
        to: '/work-schedules',
        icon: CalendarClock,
        permissions: [PERMISSIONS.WORK_SCHEDULE_READ],
      },
      {
        label: 'Attendance',
        to: '/attendance',
        icon: Clock,
        permissions: [
          PERMISSIONS.ATTENDANCE_READ_OWN,
          PERMISSIONS.ATTENDANCE_READ_TEAM,
          PERMISSIONS.ATTENDANCE_READ_ALL,
        ],
      },
      {
        // Its own grants, not attendance's: seeing where somebody is and
        // seeing whether they turned up are different questions.
        label: 'Team Location',
        to: '/location',
        icon: MapPin,
        permissions: [PERMISSIONS.LOCATION_READ_TEAM, PERMISSIONS.LOCATION_READ_ALL],
      },
      {
        // Gated on the permission to report rather than to read: this lists the
        // phones registered to you, which is the thing the terms promise you can
        // see and stop. Anybody who can share a position can reach it.
        label: 'My Phones',
        to: '/my-devices',
        icon: Smartphone,
        permissions: [PERMISSIONS.LOCATION_SHARE],
      },
      {
        // A link out, not a module. Held by every role: staff generally work in
        // UISP, and anyone without an account there just meets a login page.
        label: 'UISP',
        to: '/uisp',
        icon: Network,
        permissions: [PERMISSIONS.UISP_OPEN],
      },
      {
        label: 'Rest Day Work',
        to: '/rest-day-work',
        icon: ShieldCheck,
        permissions: [PERMISSIONS.ATTENDANCE_AUTHORISE_REST_DAY, PERMISSIONS.PAYROLL_PROCESS],
      },
      {
        label: 'Leave',
        to: '/leave',
        icon: CalendarDays,
        permissions: [
          PERMISSIONS.LEAVE_READ_OWN,
          PERMISSIONS.LEAVE_READ_TEAM,
          PERMISSIONS.LEAVE_READ_ALL,
        ],
      },
      {
        label: 'Leave Entitlements',
        to: '/leave/entitlements',
        icon: CalendarRange,
        permissions: [PERMISSIONS.LEAVE_MANAGE_BALANCES],
      },
      {
        label: 'Holidays',
        to: '/holidays',
        icon: CalendarDays,
        permissions: [PERMISSIONS.HOLIDAY_READ],
      },
    ],
  },
  {
    title: 'Compensation',
    items: [
      {
        // No permissions: everybody gets paid, so everybody sees this. The
        // endpoint behind it returns only the caller's own payslips.
        label: 'My Payslips',
        to: '/payroll/payslips',
        icon: Receipt,
      },
      {
        label: 'Payroll',
        to: '/payroll',
        icon: Banknote,
        permissions: [PERMISSIONS.PAYROLL_PROCESS, PERMISSIONS.PAYROLL_APPROVE],
      },
      {
        label: '13th Month',
        to: '/payroll/thirteenth-month',
        icon: Gift,
        permissions: [PERMISSIONS.PAYROLL_PROCESS, PERMISSIONS.PAYROLL_APPROVE],
      },
      {
        label: 'Insurance',
        to: '/insurance',
        icon: HeartPulse,
        permissions: [
          PERMISSIONS.INSURANCE_READ_OWN,
          PERMISSIONS.INSURANCE_READ_ALL,
          PERMISSIONS.INSURANCE_MANAGE,
        ],
      },
    ],
  },
  {
    // Its own section rather than an entry under Compensation: payroll is what
    // the company owes its staff, finance is what it spends. They are different
    // ledgers with different approvers, and filing them together would put the
    // fund-request screens behind a payroll permission.
    title: 'Finance',
    items: [
      {
        label: 'My Fund Requests',
        to: '/finance/fund-requests',
        icon: Banknote,
        permissions: [PERMISSIONS.FUND_REQUEST_READ_OWN],
      },
      {
        label: 'Approvals',
        to: '/finance/approvals',
        icon: ClipboardList,
        permissions: [
          PERMISSIONS.FUND_REQUEST_APPROVE_MANAGER,
          PERMISSIONS.FUND_REQUEST_APPROVE_HR,
          PERMISSIONS.FUND_REQUEST_APPROVE_MANAGEMENT,
        ],
      },
      {
        label: 'My Expense Claims',
        to: '/finance/expense-claims',
        icon: ReceiptText,
        permissions: [PERMISSIONS.EXPENSE_SUBMIT],
      },
      {
        label: 'Claim Approvals',
        to: '/finance/expense-claims/approvals',
        icon: ClipboardList,
        permissions: [
          PERMISSIONS.FUND_REQUEST_APPROVE_MANAGER,
          PERMISSIONS.FUND_REQUEST_APPROVE_HR,
          PERMISSIONS.FUND_REQUEST_APPROVE_MANAGEMENT,
          PERMISSIONS.REIMBURSEMENT_PAY,
        ],
      },
      {
        label: 'Release',
        to: '/finance/release',
        icon: Wallet,
        permissions: [PERMISSIONS.FUND_RELEASE],
      },
      {
        label: 'Liquidations',
        to: '/finance/liquidations',
        icon: ReceiptText,
        permissions: [PERMISSIONS.LIQUIDATION_VERIFY],
      },
      {
        label: 'Settlements',
        to: '/finance/settlements',
        icon: Scale,
        permissions: [PERMISSIONS.FUND_RELEASE, PERMISSIONS.REIMBURSEMENT_PAY],
      },
      {
        label: 'Accountability',
        to: '/finance/accountability',
        icon: ScrollText,
        permissions: [PERMISSIONS.FINANCE_REPORT_VIEW],
      },
      {
        label: 'Fuel',
        to: '/finance/fuel',
        icon: Fuel,
        permissions: [PERMISSIONS.FUEL_SUBMIT, PERMISSIONS.FUEL_READ_ALL],
      },
      {
        label: 'Budgets',
        to: '/finance/budgets',
        icon: Target,
        permissions: [PERMISSIONS.BUDGET_READ],
      },
      {
        label: 'Expense Report',
        to: '/finance/expense-report',
        icon: BarChart3,
        permissions: [PERMISSIONS.FINANCE_REPORT_VIEW],
      },
      {
        label: 'Vehicles',
        to: '/finance/vehicles',
        icon: Car,
        permissions: [PERMISSIONS.VEHICLE_READ],
      },
      {
        label: 'Expense Categories',
        to: '/finance/expense-categories',
        icon: Tag,
        permissions: [PERMISSIONS.FINANCE_SETTINGS_MANAGE],
      },
    ],
  },
  {
    title: 'Development',
    items: [
      {
        label: 'Performance',
        to: '/performance',
        icon: Target,
        permissions: [
          PERMISSIONS.PERFORMANCE_READ_OWN,
          PERMISSIONS.PERFORMANCE_READ_TEAM,
          PERMISSIONS.PERFORMANCE_READ_ALL,
          PERMISSIONS.PERFORMANCE_MANAGE,
        ],
      },
      {
        label: 'Training',
        to: '/training',
        icon: GraduationCap,
        permissions: [
          PERMISSIONS.TRAINING_READ_OWN,
          PERMISSIONS.TRAINING_READ_ALL,
          PERMISSIONS.TRAINING_MANAGE,
        ],
      },
      {
        label: 'Awards',
        to: '/awards',
        icon: Award,
        permissions: [PERMISSIONS.AWARD_READ],
      },
      {
        label: 'Disciplinary',
        to: '/disciplinary',
        icon: ShieldAlert,
        permissions: [PERMISSIONS.DISCIPLINARY_READ_ALL, PERMISSIONS.DISCIPLINARY_READ_OWN],
      },
    ],
  },
  {
    title: 'Workplace',
    items: [
      {
        // No permissions: everybody who works here may talk to each other.
        // Whether the company has switched chat on at all is decided by the
        // sidebar from the server's answer, not by a grant.
        label: 'Chat',
        to: '/chat',
        icon: MessageCircle,
      },
      {
        label: 'Documents',
        to: '/documents',
        icon: FileText,
        permissions: [
          PERMISSIONS.DOCUMENT_READ_OWN,
          PERMISSIONS.DOCUMENT_READ_ALL,
          PERMISSIONS.DOCUMENT_MANAGE,
        ],
      },
      {
        // Above the register, and on the weaker grant, so it is the entry an
        // ordinary employee sees — ASSET_READ_OWN is all most of them hold.
        label: 'My Assets',
        to: '/assets/mine',
        icon: Laptop,
        permissions: [PERMISSIONS.ASSET_READ_OWN, PERMISSIONS.ASSET_READ_ALL],
      },
      {
        label: 'Assets',
        to: '/assets',
        icon: Laptop,
        permissions: [PERMISSIONS.ASSET_READ_ALL, PERMISSIONS.ASSET_MANAGE],
      },
      {
        label: 'Requests',
        to: '/requests',
        icon: ClipboardList,
        permissions: [
          PERMISSIONS.REQUEST_READ_OWN,
          PERMISSIONS.REQUEST_READ_TEAM,
          PERMISSIONS.REQUEST_READ_ALL,
        ],
      },
      {
        label: 'Announcements',
        to: '/announcements',
        icon: Megaphone,
        permissions: [PERMISSIONS.ANNOUNCEMENT_READ],
      },
      {
        label: 'Resignation',
        to: '/resignation',
        icon: LogOut,
        permissions: [PERMISSIONS.RESIGNATION_READ_OWN, PERMISSIONS.RESIGNATION_READ_ALL],
      },
    ],
  },
  {
    title: 'Administration',
    items: [
      {
        label: 'Reports',
        to: '/reports',
        icon: BarChart3,
        permissions: [PERMISSIONS.REPORT_VIEW, PERMISSIONS.REPORT_EXPORT],
      },
      {
        label: 'User Accounts',
        to: '/users',
        icon: UserCog,
        permissions: [PERMISSIONS.USER_READ],
      },
      {
        label: 'Permissions',
        to: '/permissions',
        icon: ShieldCheck,
        permissions: [PERMISSIONS.PERMISSION_MANAGE],
      },
      {
        label: 'Audit Log',
        to: '/audit-log',
        icon: ScrollText,
        permissions: [PERMISSIONS.AUDIT_LOG_READ],
      },
      {
        label: 'Reference Data',
        to: '/reference-data',
        icon: Database,
        // Every permission that unlocks a section on that page must appear
        // here. A section the user can edit behind a link they cannot see is
        // indistinguishable, to them, from the feature not existing.
        permissions: [
          PERMISSIONS.AWARD_MANAGE,
          PERMISSIONS.TRAINING_MANAGE,
          PERMISSIONS.INSURANCE_MANAGE,
          PERMISSIONS.PERFORMANCE_MANAGE,
          PERMISSIONS.REQUEST_PROCESS,
          PERMISSIONS.ASSET_MANAGE,
          PERMISSIONS.PAYROLL_MANAGE_COMPONENTS,
        ],
      },
      {
        label: 'Settings',
        to: '/settings',
        icon: Settings,
        permissions: [PERMISSIONS.SETTING_READ, PERMISSIONS.SETTING_MANAGE],
      },
    ],
  },
];

/** Does `pathname` sit on, or under, the route `to`? */
function matchesPath(to: string, pathname: string): boolean {
  // The dashboard is a prefix of every route, so it only ever matches exactly.
  if (to === '/') return pathname === '/';
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * The title of the section holding the page currently open, or null.
 *
 * Matched by prefix, so a detail route with no nav entry of its own — an
 * employee's profile, say — still resolves to the group its list lives in.
 * The longest match wins: `/leave/entitlements` belongs to Leave Entitlements,
 * not to the `/leave` item it happens to sit under.
 */
export function activeSectionTitle(sections: NavSection[], pathname: string): string | null {
  let best: { title: string; length: number } | null = null;

  for (const section of sections) {
    for (const item of section.items) {
      if (!matchesPath(item.to, pathname)) continue;
      if (!best || item.to.length > best.length) {
        best = { title: section.title, length: item.to.length };
      }
    }
  }

  return best?.title ?? null;
}

/**
 * Drops items the user cannot reach, then drops sections left empty.
 *
 * A heading with nothing under it is a dead giveaway that features exist which
 * the user cannot see, and it wastes vertical space in the sidebar.
 */
export function visibleSections(
  sections: NavSection[],
  canAny: (...permissions: Permission[]) => boolean,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.permissions?.length || canAny(...item.permissions),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/** Flat lookup of path → label, for breadcrumbs. */
export const NAV_LABELS: Record<string, string> = Object.fromEntries(
  NAV_SECTIONS.flatMap((section) => section.items.map((item) => [item.to, item.label])),
);
